import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {Panel, PanelFooter, PanelFooterPrimaryDefaultAction, PanelHeader} from '@Components/panel';
import {useUpdateEffect} from 'ahooks';
import {Text, TextSize} from '@Components/text';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import {Size} from '@Components/button';
import {showEditorNotificationMessageGrowl, updateOptionsPanelHeight} from '@Components/poster-editor/library/poster-editor-library';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';

interface PosterEditingSlideshowApplyPropertiesPanelProps {
  panelId: string;
}

export default function PosterEditorSlideshowApplyPropertiesPanel({...props}: PosterEditingSlideshowApplyPropertiesPanelProps): ReactElement | null {
  const onClose = useDefaultPanelClose(props.panelId);
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeSlideUID = useGetSelectedSlideId();
  const activeSlide = activeSlideUID && activeItem?.isSlideshow() ? activeItem.getSelectedSlide() : null;

  const [textRef, setTextRef] = useState<HTMLDivElement | null>(null);

  useUpdateEffect((): void => {
    onClose();
  }, [activeItemId, activeSlideUID]);

  useEffect((): void => {
    if (!textRef) {
      return;
    }
    updateHeightInRedux(textRef.clientHeight);
  }, [textRef]);

  const updateHeightInRedux = (contentHeight: number): void => {
    const height = contentHeight + 16 + 16 + 64 + 60;
    updateOptionsPanelHeight(props.panelId, `${height}px`);
  };

  if (!activeSlide || !activeItem?.isSlideshow()) {
    return null;
  }

  const getHeader = (): ReactElement => {
    return <PanelHeader onClose={onClose} title={window.i18next.t('pmwjs_apply_to_all_slides')} />;
  };

  const getText = (): string => {
    switch (activeSlide.gitype) {
      case ITEM_TYPE.TEXTSLIDE:
        return window.i18next.t('pmwjs_apply_to_all_text_slides');
      case ITEM_TYPE.IMAGESLIDE:
        return window.i18next.t('pmwjs_apply_to_all_photo_slides');
      case ITEM_TYPE.VIDEOSLIDE:
        return window.i18next.t('pmwjs_apply_to_all_video_slides');
      default:
        throw new Error('Unhandled ');
    }
  };

  const getContent = (): ReactElement => {
    return (
      <div className="spacing-p-4 _fit-height">
        <div ref={setTextRef}>
          <Text dangerouslySetInnerHTML val={getText()} size={TextSize.SMALL} />
        </div>
      </div>
    );
  };

  const onApply = async (): Promise<void> => {
    await activeItem.applyStylesToAllSlides();
    showEditorNotificationMessageGrowl(window.i18next.t('pmwjs_applied'));
  };

  const getFooter = (): ReactElement => {
    return (
      <PanelFooter
        fullWidthPrimaryActions
        primaryActions={[
          <PanelFooterPrimaryDefaultAction
            onClick={(): void => {
              void onApply();
            }}
            key="apply"
            size={Size.SMALL}
            text={window.i18next.t('pmwjs_apply')}
          />,
        ]}
      />
    );
  };

  return <Panel panelHeader={getHeader()} panelFooter={getFooter()} panelContent={getContent()} />;
}
